import { Router } from '~/core';

export function bytesToString(bytes: number, short = false): [number, string] {
    if (bytes === 0) return [0, short ? 'B' : 'bytes'];

    const k = 1024;
    const sizes = short ? ['B', 'KB', 'MB', 'GB', 'TB'] : ['bytes', 'kilobytes', 'megabytes', 'gigabytes', 'terabytes'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return [parseFloat((bytes / Math.pow(k, i)).toFixed(1)), sizes[i]];
}

export function simpleBytesToString(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(1));
    return `${size}${sizes[i]}`;
}

// A naive implementation to filter out any bad (= no reason to have) parameters for that specific route. Ideally,
// this should consider the full path somehow, but I can't be bothered to figure that out right now :/
export function getRouteParams(path: string, routeParams: Record<string, any>) {
    const params: Record<string, string | string[]> = {};
    for (const key in routeParams) {
        if (path.indexOf(`:${key}`) === -1) continue;

        params[key] = routeParams[key];
    }

    return params;
}

export function normalizeNamespaceAndPath(data: string): string {
    data = data.replaceAll('?', ''); // not a clue what these were ever for

    if (data.includes(':')) return data;

    // Use router directly to get current route name
    const currentRouteName = Router.currentRoute.value.name;

    return `${currentRouteName?.toString().split('.').shift()}:${data}`;
}
export function underscoreToCamelCase(value: string): string {
    let key = '';
    let capitalize = false;
    for (const char of value) {
        if (char === '_') capitalize = true;
        else {
            key += capitalize ? char.toUpperCase() : char;
            capitalize = false;
        }
    }

    return key;
}

export function convertDataToCamelCase(data: Record<string, any>): any {
    if (Array.isArray(data)) return data;

    const res: Record<string, any> = {};
    for (const key in data) {
        res[underscoreToCamelCase(key)] = data[key] instanceof Object ? convertDataToCamelCase(data[key]) : data[key];
    }

    return res;
}

export function camelCaseToUnderscore(value: string): string {
    let key = '';
    for (const char of value) {
        if (key === '') {
            key += char.toLowerCase();
            continue;
        }

        key += char === char.toUpperCase() && /^[a-zA-Z0-9]+$/.test(char) ? `_${char.toLowerCase()}` : char;
    }

    return key;
}

export function convertDataToUnderscore(data: Record<string, any>): any {
    if (Array.isArray(data)) return data;

    const res: Record<string, any> = {};
    for (const key in data) {
        res[camelCaseToUnderscore(key)] = data[key] instanceof Object ? convertDataToUnderscore(data[key]) : data[key];
    }

    return res;
}

export function getSteamID64(input: string): bigint | null {
    if (/^\d+$/.test(input)) {
        return BigInt(input);
    }

    const parts = input.split(':');
    if (parts.length !== 3 || !parts[0].toLowerCase().startsWith('steam_')) {
        return null;
    }

    const authServer = BigInt(parts[1]);
    const accountNumber = BigInt(parts[2]);

    return accountNumber * 2n + authServer + 76561197960265728n;
}

export function getSteamID(input: string | number | bigint): string | null {
    if (typeof input === 'string' && input.startsWith('STEAM_')) {
        return input;
    }

    let steamid64: bigint;

    if (typeof input === 'string') {
        try {
            steamid64 = BigInt(input);
        } catch {
            return null;
        }
    } else {
        steamid64 = BigInt(input);
    }

    const y = steamid64 % 2n;
    const accountId = (steamid64 - 76561197960265728n - y) / 2n;

    return `STEAM_0:${y}:${accountId}`;
}

export function eqSteamId(input: string, steamid64: string | number): boolean {
    if (input == steamid64) {
        return true;
    }

    const match = input.match(/^steam_\d:(0|1):(\d+)$/i);
    if (match) {
        const [_, y, z] = match;

        const queryAccountNumber = (Number(z) << 1) + Number(y);

        let bigInt;
        try {
            bigInt = BigInt(steamid64);
        } catch {
            return false;
        }

        const accountNumber = Number(bigInt & 0xffffffffn);

        if (accountNumber === queryAccountNumber) {
            return true;
        }
    }

    return false;
}

export function isValidSteamID64(value: string | number | bigint): boolean {
    let steamid64: bigint;

    if (typeof value === 'string') {
        try {
            steamid64 = BigInt(value);
        } catch {
            return false;
        }
    } else {
        steamid64 = BigInt(value);
    }

    return steamid64 >= 76561197960265728n && steamid64 <= 18446744073709551615n;
}

// @ts-expect-error - String.fromCharCode.apply expects number[] but Uint8Array is compatible
export const bufferToString = (value: ArrayBuffer): string => btoa(String.fromCharCode.apply(null, new Uint8Array(value)));
export const stringToBuffer = (value: string): ArrayBuffer => Uint8Array.from(atob(value), (c) => c.charCodeAt(0)).buffer;
